import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { useLocation } from "wouter";
import { TooltipProvider } from "@/components/ui/tooltip";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Layout from "./components/Layout";
import PageTransition from "./components/PageTransition";
import { OffertProvider } from "./components/OffertModal";
import { trackPageView } from "@/lib/gtm";
import { trackPageView as fbqPageView } from "@/lib/metaPixel";
import { whenBrowserIdle } from "@/lib/whenBrowserIdle";

function ScrollToTop() {
  const [location] = useLocation();
  const prevLocation = useRef(location);

  useEffect(() => {
    if (prevLocation.current !== location) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      void import("@shared/seoData.json").then(module => {
        if (window.location.pathname === location) {
          const map = module.default as Record<string, { title: string }>;
          trackPageView(location, map[location]?.title);
        }
      });
      fbqPageView();
      prevLocation.current = location;
    }
  }, [location]);

  return null;
}

/** Sonner is unused on first paint — load after idle so it leaves the Lighthouse JS budget. */
function ClientToaster() {
  const [Toaster, setToaster] = useState<ComponentType | null>(null);
  useEffect(() => {
    return whenBrowserIdle(() => {
      void import("@/components/ui/sonner").then(mod => {
        setToaster(() => mod.Toaster);
      });
    });
  }, []);
  if (!Toaster) return null;
  return <Toaster />;
}

/**
 * Shared chrome for SSR and the browser so hydrateRoot sees the same tree.
 * Route tables stay outside — homepage is eager, other pages stay lazy on the client.
 */
export default function AppShell({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <OffertProvider>
            <ClientToaster />
            <Layout>
              <ScrollToTop />
              <PageTransition>{children}</PageTransition>
            </Layout>
          </OffertProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
